import React from "react";
import { motion } from "framer-motion";
import { Pencil, Trash2, Home, Briefcase, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import type { Address } from "@/api/models/api-types";

export interface AddressCardProps {
  address: Address;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const AddressCard: React.FC<AddressCardProps> = ({
  address,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = React.useState(false);

  // Icon based on label
  const getIconForLabel = (label: string) => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes("home") || lowerLabel.includes("ev")) {
      return <Home className="w-4 h-4" />;
    }
    if (lowerLabel.includes("office") || lowerLabel.includes("iş") || lowerLabel.includes("work")) {
      return <Briefcase className="w-4 h-4" />;
    }
    return <MapPin className="w-4 h-4" />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onSelect}
      className={cn(
        "relative p-4 rounded-[12px] border-2 cursor-pointer transition-all duration-200",
        "bg-bg-primary dark:bg-gray-800",
        isSelected
          ? "border-primary shadow-md ring-2 ring-primary/20"
          : "border-border-secondary dark:border-gray-600 hover:border-primary/50 hover:shadow-sm"
      )}
    >
      {/* Radio Button */}
      <div className="flex items-start gap-3">
        <div className="flex items-center pt-0.5">
          <div
            className={cn(
              "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
              isSelected
                ? "border-primary bg-primary"
                : "border-gray-300 dark:border-gray-600"
            )}
          >
            {isSelected && (
              <div className="w-2.5 h-2.5 rounded-full bg-white" />
            )}
          </div>
        </div>

        {/* Address Content */}
        <div className="flex-1 min-w-0">
          {/* Label with icon */}
          <div className="flex items-center gap-2 mb-2">
            <div className="text-primary">{getIconForLabel(address.label)}</div>
            <h4 className="font-semibold text-sm text-foreground">
              {address.label}
            </h4>
            {address.isDefault && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-medium">
                {t("checkout.defaultAddress")}
              </span>
            )}
          </div>

          {/* Name */}
          <p className="text-sm font-medium text-foreground mb-1">
            {address.firstName} {address.lastName}
          </p>

          {/* Address */}
          <p className="text-sm text-muted-foreground mb-1">
            {address.addressLine1}
            {address.addressLine2 && `, ${address.addressLine2}`}
          </p>

          {/* City, State, Postal Code */}
          <p className="text-sm text-muted-foreground">
            {address.city}, {address.state} {address.postalCode}
          </p>

          {/* Country */}
          <p className="text-sm text-muted-foreground">{address.country}</p>

          {/* Phone (if exists) */}
          {address.phone && (
            <p className="text-sm text-muted-foreground mt-1">
              {address.phone}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        {/* Desktop: Show on hover, Mobile: Always show */}
        <div
          className={cn(
            "flex items-center gap-1 transition-opacity duration-200",
            "md:opacity-0 md:group-hover:opacity-100",
            isHovered && "md:opacity-100",
            "opacity-100" // Always visible on mobile
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
          >
            <Pencil className="w-4 h-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

